//
// # Workspace React Context
//
// Provides the Workspace instance to React components,
// and a hook to subscribe to its observable state.
//

import React, { createContext, useContext, useSyncExternalStore } from "react";
import { Workspace } from "./workspace";
import { WorkspaceSnapshot } from "./types";

const WorkspaceContext = createContext<Workspace | null>(null);

export interface WorkspaceProviderProps {
  workspace: Workspace;
  children: React.ReactNode;
}

// Provides the Workspace instance to all descendant components.
export function WorkspaceProvider({
  workspace,
  children,
}: WorkspaceProviderProps) {
  return (
    <WorkspaceContext.Provider value={workspace}>
      {children}
    </WorkspaceContext.Provider>
  );
}

// Access the Workspace instance from React components.
// Must be used within a WorkspaceProvider.
export function useWorkspace(): Workspace {
  const ws = useContext(WorkspaceContext);
  // This is a programming error (component outside provider), not a runtime failure.
  if (!ws) {
    throw new Error("useWorkspace must be used within a WorkspaceProvider");
  }
  return ws;
}

// Subscribe to workspace state changes.
// Returns an immutable snapshot that updates when tabs change.
export function useWorkspaceSnapshot(): WorkspaceSnapshot {
  const ws = useWorkspace();
  return useSyncExternalStore(ws.subscribe, ws.getSnapshot);
}
